import React, { useEffect, useState } from "react";
import { Col, Container, Modal, ModalBody, Row } from "reactstrap";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/images/logo.png";
import LoginForm from "../../components/Auth/LoginForm";
import { getToken } from "../../services/AuthToken";
import Footer from "../../Layout/VerticalLayout/Footer";
import "./LandingPage.css";

const BrandLockup = ({ logoHeight = 62, wordClassName = "brand-word" }) => (
  <span className="brand-lockup">
    <img src={logo} alt="Valitan" height={logoHeight} />
    <span className={wordClassName}>alitan</span>
  </span>
);

const LandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const token = getToken();
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    document.title = "Valitan IMS";
    document.body.classList.add("landing-page-body");

    return () => {
      document.body.classList.remove("landing-page-body");
    };
  }, []);

  const closeLoginModal = () => {
    setLoginOpen(false);
  };

  const openLoginModal = () => {
    setLoginOpen(true);
  };

  if (token && (location.pathname === "/" || location.pathname === "/login")) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="valitan-landing">
      <div className="hero-shell py-4 py-lg-5">
        <Container className="py-lg-4">
          <div className="d-flex justify-content-between align-items-center mb-5 gap-3 flex-wrap">
            <Link to="/" className="text-decoration-none">
              <BrandLockup />
            </Link>
            <div className="d-flex gap-2">
              {token ? (
                <Link to="/dashboard" className="cta-primary">
                  Open Dashboard
                </Link>
              ) : (
                <button type="button" className="cta-primary border-0" onClick={openLoginModal}>
                  Sign In
                </button>
              )}
            </div>
          </div>

          <div className="glass-card rounded-5 p-4 p-lg-5">
            <div className="hero-grid">
              <div>
                <div className="chip mb-4">
                  <span className="rounded-circle bg-warning" style={{ width: 10, height: 10 }} />
                  VALITAN INFORMATION MANAGEMENT SYSTEM
                </div>
                <h1 className="hero-title fw-bold mb-4">
                  Valuation work, <span>review flow,</span> and control in one place.
                </h1>
                <p className="hero-copy mb-4">
                  Valitan IMS brings project setup, valuation processing, reviewer visibility, and
                  reporting together in a single operational environment built for structured teams.
                </p>

                <div className="d-flex flex-wrap gap-3 mb-5">
                  {!token && (
                    <button
                      type="button"
                      className="cta-primary border-0"
                      onClick={openLoginModal}
                    >
                      Launch Secure Login
                    </button>
                  )}
                  <a href="#capabilities" className="cta-secondary">
                    Explore Capabilities
                  </a>
                </div>
              </div>

              <div>
                <div className="feature-panel">
                  <p
                    className="text-uppercase small fw-bold mb-3"
                    style={{ letterSpacing: "0.14em", color: "#c8d8ff" }}
                  >
                    Operational Focus
                  </p>
                  <h3 className="fw-bold mb-3">
                    Designed for teams managing complex valuation lifecycles.
                  </h3>
                  <p className="mb-4 text-white-50">
                    From user assignments and project mapping to valuation records and data review,
                    the system keeps work visible and constrained where it should be.
                  </p>
                  <div className="d-grid gap-3">
                    {[
                      "Structured dashboards for project and valuation oversight",
                      "Role-aware navigation for cleaner access boundaries",
                      "Central handling for not-found, permission, and server error states",
                    ].map((item) => (
                      <div
                        key={item}
                        className="d-flex align-items-start gap-3 rounded-4 p-3"
                        style={{ background: "rgba(255,255,255,0.08)" }}
                      >
                        <i className="ri-checkbox-circle-fill mt-1" style={{ color: "#f1c75b" }} />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="data-collection-section mt-4 mt-lg-5">
            <div className="section-heading mb-4">
              <p className="section-kicker mb-2">Data Collection</p>
              <h2 className="section-title mb-0">Operational inputs aligned for field-to-review workflow.</h2>
            </div>

            <div className="stat-strip">
              <div className="workflow-card">
                <div className="workflow-number">
                  <span>01</span>
                </div>
                <strong>Collection and Verification</strong>
                <span className="text-muted">
                  Capture field data, validate records, and keep source information ready for downstream work.
                </span>
              </div>
              <div className="workflow-card">
                <div className="workflow-number">
                  <span>02</span>
                </div>
                <strong>Reviews</strong>
                <span className="text-muted">
                  Control reviewer access, comments, and progression points.
                </span>
              </div>
              <div className="workflow-card">
                <div className="workflow-number">
                  <span>03</span>
                </div>
                <strong>Analysis</strong>
                <span className="text-muted">
                  Structure valuation findings, compare evidence, and support consistent assessment workflows.
                </span>
              </div>
              <div className="workflow-card">
                <div className="workflow-number">
                  <span>04</span>
                </div>
                <strong>Reporting</strong>
                <span className="text-muted">
                  Prepare reports, outputs, and supporting documents for delivery and audit visibility.
                </span>
              </div>
            </div>
          </section>

          <section id="capabilities" className="mt-4 mt-lg-5">
            <div className="section-heading mb-4">
              <p className="section-kicker mb-2">Our Services</p>
              <h2 className="section-title mb-0">Core valuation service lines supported through one operating system.</h2>
            </div>

            <Row className="g-4">
              {[
                [
                  "Asset Valuation",
                  "Support collection, review, and analysis workflows for asset-focused valuation programs.",
                  "ri-building-line",
                ],
                [
                  "Compensation Valuation",
                  "Manage compensation valuation cases, supporting records, and review flow for compensation-focused assignments.",
                  "ri-scales-3-line",
                ],
                [
                  "Mortgage Valuation",
                  "Handle valuation records, linked entities, and downstream report preparation.",
                  "ri-bank-line",
                ],
                [
                  "Security & Roles",
                  "Control what each role sees while preserving a consistent application shell.",
                  "ri-shield-keyhole-line",
                ],
            ].map(([title, body, icon]) => (
              <Col lg="3" md="6" key={title}>
                <div className="service-card h-100">
                  <div className="service-icon">
                    <i className={icon} />
                  </div>
                  <h5 className="fw-bold mb-3 text-dark">{title}</h5>
                  <p className="text-muted mb-0">{body}</p>
                </div>
              </Col>
            ))}
            </Row>
          </section>
        </Container>
      </div>

      <div className="landing-footer-shell">
        <Footer />
      </div>

      <Modal
        isOpen={loginOpen}
        toggle={closeLoginModal}
        centered
        size="xl"
        className="valitan-login-modal border-0"
        backdrop="static"
        keyboard={false}
      >
        <ModalBody className="p-0 overflow-hidden rounded-4">
          <LoginForm
            onClose={closeLoginModal}
            onSuccess={() => navigate("/dashboard")}
            submitLabel="Login"
          />
        </ModalBody>
      </Modal>
    </div>
  );
};

export default LandingPage;
